import { t } from '@lingui/macro'
import { CandidateDTO } from '@om1/aspen-api'
import { CandidateIndexDTO } from '@om1/aspen-api/codegen/client/models/CandidateIndexDTO'

/**
 * Coordinator-side .ics for a candidate's appointment (APP-1221).
 *
 * ## What was wrong before
 *
 * The previous version had two independent bugs that cancelled out for exactly
 * one audience — an Eastern coordinator looking at an Eastern patient:
 *
 * 1. It formatted the UTC instant into an **Eastern wall-clock string**
 *    regardless of the patient's actual timezone, so a Pacific patient's 9am
 *    slot was written as 12pm.
 * 2. It then handed that string to `calendar-link`'s `ics()` with **no timezone
 *    marker at all** — a naive `2026-09-03T12:00:00`. Calendar clients read a
 *    naive datetime as *the viewer's own local time*, so the event landed three
 *    hours out for anyone not in Eastern.
 *
 * Because those two errors ran in opposite directions, the file looked correct
 * to the people most likely to check it. That is why it survived.
 *
 * ## What this does instead
 *
 * `DTSTART`/`DTEND` are emitted as **UTC instants**. A UTC instant is
 * unambiguous, and every client renders it in the viewer's own zone — which is
 * what you actually want for a meeting. Deliberately NOT `DTSTART;TZID=`: doing
 * that correctly needs a `VTIMEZONE` block with DST rules, and getting it subtly
 * wrong moves a clinical appointment by an hour. `X-WR-TIMEZONE` carries the
 * patient's zone for clients that surface it, and the patient's local time is
 * spelled out in the description so a coordinator can read it without doing
 * timezone arithmetic.
 *
 * Prefers the booked appointment over the legacy preferred-contact window, since
 * the appointment is a commitment and the window never was.
 *
 * NOTE: this mirrors the patient-side generator in central-site
 * (`RegistrationThankYou.tsx`). Two copies is one too many — but they live in
 * different apps and extracting a shared package is a wider change than this
 * ticket. Worth doing when something next touches either.
 */

type Candidate = CandidateDTO | CandidateIndexDTO

const ICS_FILENAME = 'OM1_Study_Intake_Call.ics'

/** RFC 5545 requires these escaped; the description carries a URL and free text. */
const escapeIcsText = (value: string): string =>
    value
        .replace(/\\/g, '\\\\')
        .replace(/[;,]/g, (c) => `\\${c}`)
        .replace(/\r?\n/g, '\\n')

const utcStamp = (date: Date): string =>
    date
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '')

/** The appointment time in the patient's own zone, for the description. */
const describeInPatientZone = (start: Date, timeZone: string): string => {
    try {
        return start.toLocaleString('en-US', {
            timeZone,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        })
    } catch {
        // An unknown IANA id would otherwise throw and lose the whole download.
        return start.toISOString()
    }
}

export function generateCalendarInvite(
    candidate: Candidate,
    enqueueSnackbar: (message: string, options: { variant: 'error' | 'success' | 'warning' | 'info' }) => void
) {
    // The booked appointment is the truth. The preferred-contact window is only
    // a fallback for candidates who registered before the scheduler existed and
    // have not been backfilled yet (APP-1222).
    const appointment = candidate.appointment
    const startValue = appointment?.startAt ?? candidate.preferredContactStartDatetime
    const endValue = appointment?.endAt ?? candidate.preferredContactEndDatetime

    if (!startValue || !endValue) {
        enqueueSnackbar(t`No appointment or preferred contact time set for this candidate.`, { variant: 'warning' })
        return
    }

    const start = new Date(startValue)
    const end = new Date(endValue)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        // Previously an unparseable date produced an .ics containing "Invalid
        // Date", which calendar clients silently reject — the coordinator saw a
        // successful download and got nothing.
        enqueueSnackbar(t`This appointment has an invalid date and cannot be downloaded.`, { variant: 'error' })
        return
    }

    // Patient's zone, or Eastern when unknown — NULL for anyone registered
    // before APP-1210 added the column. Falling back to Eastern is the old
    // behaviour, but now it applies only to the description, never to the
    // instant, so a wrong guess no longer moves the appointment.
    const patientZone = appointment?.timezone ?? candidate.timezone ?? 'America/New_York'
    const candidateUrl = `${window.location.origin}/candidates/${candidate.id}`

    const descriptionLines = [`${describeInPatientZone(start, patientZone)} (patient's local time)`, candidateUrl]

    // The patient's number, right in the invite (APP-1331).
    //
    // Studies that do not use conferencing are calling the patient, so the
    // number is the single thing the coordinator needs at the moment the
    // reminder fires. Without it they had the candidate link above and had to
    // open aspen to find it -- fine at a desk, not while a call is due.
    //
    // Appended rather than unshifted so it does not displace the meeting URL
    // for studies that still have one.
    if (candidate.phone) {
        descriptionLines.push(t`Phone: ${candidate.phone}`)
    }

    if (appointment?.meetingUrl) {
        descriptionLines.unshift(appointment.meetingUrl)
    }

    // A stable UID means a re-download updates the coordinator's existing event
    // rather than creating a duplicate every time they click.
    const uid = appointment?.calcomBookingUid ? `${appointment.calcomBookingUid}@om1.com` : `om1-candidate-${candidate.id}@om1.com`

    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//OM1//Aspen//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        `X-WR-TIMEZONE:${patientZone}`,
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${utcStamp(new Date())}`,
        `DTSTART:${utcStamp(start)}`,
        `DTEND:${utcStamp(end)}`,
        `SUMMARY:${escapeIcsText(t`OM1 Study Intake Call`)}`,
        `DESCRIPTION:${escapeIcsText(descriptionLines.join('\n'))}`
    ]
    if (appointment?.meetingUrl) {
        lines.push(`LOCATION:${escapeIcsText(appointment.meetingUrl)}`)
    }
    lines.push('END:VEVENT', 'END:VCALENDAR')

    // CRLF is required by RFC 5545; some Outlook versions reject LF-only files.
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
    a.download = ICS_FILENAME
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(href)
}
